import { isNull, isUndefined } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { NettingPage, NettingEngineValidGoverningLaw, NettingResponseData, NettingEngineTableView, ProductTypeCovered, SelectedJurisdictionDetails, NettingEngineAgreement, NettingEngineData, ISDANettingEngineData, ISDAJurisdictionalNettingDetails, JurisdictionalNettingInformation, NettingResultsEmailDetails } from './types';
import { ArkDocument, AnalysisView } from '../../../documents/store/types';
import { getOpinionNettingEngineRoot } from '../../store/selectors';
import { AgreementType } from '../../../admin/documents/store/types';

export const getNettingPage = (state: ApplicationState): NettingPage => getOpinionNettingEngineRoot(state).nettingPage;

export const getCurrentNettingEngineData = (state: ApplicationState): NettingEngineData | null => getOpinionNettingEngineRoot(state).currentNettingEngineData;
export const getNettingEngineAgreementType = (state: ApplicationState): AgreementType | null => getOpinionNettingEngineRoot(state).selectedAgreementType;

export const getNettingEngineResults = (state: ApplicationState): NettingResponseData | null => getOpinionNettingEngineRoot(state).engineResults;

export const getTheoreticalQuestion = (state: ApplicationState): string | null => getOpinionNettingEngineRoot(state).theoreticalQuestion;

export const getIsCalculatingResults = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).calculatingResults;

export const getSurveyQuestionIndex = (state: ApplicationState): number => getOpinionNettingEngineRoot(state).surveyQuestionIndex;

export const getShowSummary = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).showSummary;

export const getLeiModalOpen = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).leiModalOpen;

export const getIsChangingQuestion = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).isChangingQuestion;

export const getNettingAssumptionsModalOpen = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).nettingAssumptionsModalOpen;

export const getNettingAssumptions = (state: ApplicationState): string[] => {
    const currentData = getCurrentNettingEngineData(state);
    const currentSelectedAgreementType = getNettingEngineAgreementType(state);
    const result: string[] = ['The analysis assumes that all required additional provisions are included'];
    if (isNull(currentSelectedAgreementType)) {
        return result;
    }
    if (currentSelectedAgreementType === AgreementType.ISDA && currentData) {
        const { governingLaw, counterpartyOffices, agreement, paymentMethod, counterparty, sectionTenAApplies, automaticEarlyTerminationApplicable } = currentData as ISDANettingEngineData;
        if (governingLaw && !Object.values(NettingEngineValidGoverningLaw).includes(governingLaw as NettingEngineValidGoverningLaw)) {
            result.push(`The agreement is enforceable as a matter of local law (${governingLaw}).`);
        }
        if (counterpartyOffices.length === 0) {
            result.push('The counterparty is operating from an office in the jurisdiction of its incorporation.');
        }
        if (agreement === NettingEngineAgreement.ISDA_1992_MASTER && !paymentMethod) {
            result.push('The analysis assumes the Second Method shall apply for payment method on early termination.');
        }
        if (isNull(counterparty.subCounterpartyTypeId)) {
            result.push('In this analysis, the parent counterparty type is considered, due to an absence of a sub counterparty.');
        } else if (counterpartyOffices.length > 0) {
            result.push('Where possible, the analysis uses the selected sub counterparty type; otherwise, it defaults to the parent counterparty type.');
        }
        if (isUndefined(sectionTenAApplies)) {
            result.push('Section 10(a) is applicable');
        }
        if (isNull(automaticEarlyTerminationApplicable) || isUndefined(automaticEarlyTerminationApplicable)) {
            result.push('For the purpose of the theory, we have not made an assumption whether automatic early termination applies or not, and return whether it is required or not instead.');
        }
    }
    return result;
};

export const getUnauthorisedOpinionJurisdictions = (state: ApplicationState): string[] => {
    const results = getNettingEngineResults(state);
    if (isNull(results)) {
        return [];
    }
    const unauthorisedRequestedJurisdictions = (results.jurisdictionalInformation as JurisdictionalNettingInformation[]).filter(eachResult => !eachResult.permittedToView).map(eachResult => eachResult.jurisdiction);
    const localLawJurisdiction = getLocalLawIsValidJurisdiction(state);
    return !isNull(localLawJurisdiction) ? [...unauthorisedRequestedJurisdictions, localLawJurisdiction] : unauthorisedRequestedJurisdictions;
};

export const getLocalLawIsValidJurisdiction = (state: ApplicationState): string | null => {
    const results = getNettingEngineResults(state);
    const selectedAgreementType = getNettingEngineAgreementType(state);
    if (isNull(results)) {
        return null;
    }
    if (selectedAgreementType !== AgreementType.ISDA) {
        return null;
    }
    const currentNettingEngineData = getCurrentNettingEngineData(state) as ISDANettingEngineData | null;
    const localLawValid = (results.jurisdictionalInformation[0].details as ISDAJurisdictionalNettingDetails).localLawIsValid;
    return (isNull(localLawValid) || localLawValid.value === 'Yes') ? null : currentNettingEngineData!.governingLaw;
};

export const getEnquiredOpinions = (state: ApplicationState): string[] => getOpinionNettingEngineRoot(state).enquiredOpinions;

export const getIsCustomConclusionValue = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).isCustomConclusion;

export const getIsLoading = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).isLoading;

export const getIsOpening = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).isOpening;

export const getNettingEngineDocuments = (state: ApplicationState): ArkDocument[] => getOpinionNettingEngineRoot(state).nettingEngineDocuments;

export const getSavedNettingEngineData = (state: ApplicationState): NettingEngineData | null => getOpinionNettingEngineRoot(state).savedNettingEngineData;

export const getCurrentNettingEngineDocumentIsValid = (state: ApplicationState): boolean => {
    const currentNettingEngineDocument = getCurrentNettingEngineData(state);
    const selectedAgreementType = getNettingEngineAgreementType(state);

    if (selectedAgreementType === AgreementType.ISDA) {
        const { counterpartyNameKnown, counterparty: { entityId, jurisdiction, counterpartyClassification }, agreement, governingLaw, sectionTenAApplies, counterpartyIsMultiBranch } = currentNettingEngineDocument as ISDANettingEngineData;
        return [currentNettingEngineDocument, counterpartyNameKnown, entityId, jurisdiction, counterpartyClassification, agreement, governingLaw, counterpartyIsMultiBranch, (sectionTenAApplies || null)].every(option => !isNull(option));
    }
    // TODO add GMRA logic here
    return true;
};

export const getNettingEngineTableView = (state: ApplicationState): NettingEngineTableView => getOpinionNettingEngineRoot(state).nettingEngineTableView;

export const getDocumentSummaryView = (state: ApplicationState): AnalysisView => getOpinionNettingEngineRoot(state).documentSummaryView;

export const getSelectedProductCovered = (state: ApplicationState): ProductTypeCovered | null => getOpinionNettingEngineRoot(state).selectedProductCovered;

export const getSelectedJurisdictionDetails = (state: ApplicationState): SelectedJurisdictionDetails => getOpinionNettingEngineRoot(state).selectedJurisdictionDetails;

export const getAnswerJustificationModalOpen = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).answerJustificationModalOpen;

export const getIsDownloadingNettingEngineSummary = (state: ApplicationState): boolean => getOpinionNettingEngineRoot(state).isDownloadingNettingSummary;

export const getResultsEmailDetails = (state: ApplicationState): NettingResultsEmailDetails | null => getOpinionNettingEngineRoot(state).resultsEmailDetails;

// Table Filters and Pagination
export const getNettingEngineDocumentsPageNumber = (state: ApplicationState): number => getOpinionNettingEngineRoot(state).nettingEngineDocumentPage;
export const getTotalNettingEngineDocuments = (state: ApplicationState): number => getOpinionNettingEngineRoot(state).totalNettingEngineDocuments;
export const getNettingEngineDocumentFilters = (state: ApplicationState): TableFilters => getOpinionNettingEngineRoot(state).nettingEngineDocumentFilters;
export const getNettingEngineDocumentColumnSort = (state: ApplicationState): ColumnSort | undefined => getOpinionNettingEngineRoot(state).nettingEngineDocumentSort;
export const getNettingEngineDocumentPageSize = (state: ApplicationState): number => getOpinionNettingEngineRoot(state).nettingEngineDocumentPageSize;
